/**
 * Theme-owned Attentive → Maestro bridge for `view : popup`.
 *
 * Fires `attentiveViewPopup` for allowlisted impressions (`pre_engagement`).
 * Skips bubble impressions. Bubble expands are handled by attentive-bubble-click
 * so click can precede view. Chains with other dataLayer.push wrappers.
 */

import {
  fireViewPopupWhenImpressionCountReady,
  installDataLayerEntryListener,
  parseAttentiveEntry,
  resetDataLayerEntryListener,
  type AttentiveEntry,
} from "./attentive-data-layer";

const ATTENTIVE_SOURCE = "Attentive";
const IMPRESSIONS_EVENT = "impressions";
const EXPAND_EVENT = "sign-up unit expand";
const BUBBLE_TYPE = "bubble";
const MAESTRO_METHOD = "attentiveViewPopup";
const IMPRESSION_ALLOWLIST: ReadonlySet<string> = new Set(["pre_engagement"]);

const installState = { installed: false, originalPush: null };

function shouldFireViewPopup(entry: AttentiveEntry): boolean {
  if (entry.source !== ATTENTIVE_SOURCE) {
    return false;
  }
  // Bubble expands are owned by attentive-bubble-click so click can precede view.
  if (entry.event === EXPAND_EVENT) {
    return entry.creative_type !== BUBBLE_TYPE;
  }
  return (
    entry.event === IMPRESSIONS_EVENT &&
    entry.creative_type !== undefined &&
    IMPRESSION_ALLOWLIST.has(entry.creative_type)
  );
}

function handleEntry(entry: unknown): void {
  const attentiveEntry = parseAttentiveEntry(entry);
  if (
    !attentiveEntry ||
    !shouldFireViewPopup(attentiveEntry) ||
    !window.maestro?.fireTrackEvent
  ) {
    return;
  }
  fireViewPopupWhenImpressionCountReady(attentiveEntry, (props) =>
    window.maestro.fireTrackEvent(MAESTRO_METHOD, props)
  );
}

/** @internal Resets module state between tests. */
export function resetAttentiveViewPopupInstallForTests(): void {
  resetDataLayerEntryListener(installState);
}

/** Installs the Attentive dataLayer → Maestro view : popup bridge. */
export function installAttentiveViewPopup(): void {
  installDataLayerEntryListener(handleEntry, installState);
}
